import { useCallback } from 'react';
import { useTranslator } from '@ianesthesie-kit/core';
import {
  useCurrentHospital,
  useIsHospitalModuleEnabled
} from '@/modules/appContext';
import {
  isReminderChannel,
  parseReminderChannels,
  reminderChannelLabelKey
} from './billingLine.labels';

/**
 * True when the current hospital has the billing module on (`billingModuleEnabled`, decision 6 —
 * Billing V0 reuses the invoice module's flag). Route-level gating uses
 * `withProtectedAccess(pagePermissions.billing)`; use this for conditional UI inside pages.
 */
export const useIsBillingModuleEnabled = () =>
  useIsHospitalModuleEnabled('billing');

/**
 * True when the current hospital takes card / online payments through Stripe. Not a
 * `*ModuleEnabled` flag, hence the hospital read rather than `useIsHospitalModuleEnabled`.
 * Route-level gating uses `withProtectedAccess(pagePermissions.stripe)`; use this for
 * conditional UI inside pages.
 */
export const useIsBillingStripeEnabled = () =>
  !!useCurrentHospital().queryResult.currentData?.billingStripeEnabled;

/**
 * Formats a line's `lastReminderChannel` — a comma-joined list of channel names such as
 * `"EMAIL,WHATSAPP"` for the default D+45 step — into a translated, comma-separated label
 * ("E-mail, WhatsApp"). Shared by the dashboard's last-reminder cell and the PDV line row.
 * Returns `undefined` when there is nothing to show; a token the frontend does not know is
 * rendered as its raw enum name rather than as a missing translation key.
 */
export const useReminderChannelsLabel = () => {
  const translate = useTranslator();

  return useCallback(
    (lastReminderChannel: string | undefined): string | undefined => {
      const tokens = parseReminderChannels(lastReminderChannel);
      if (!tokens.length) {
        return undefined;
      }
      return tokens
        .map((token) =>
          isReminderChannel(token)
            ? translate(reminderChannelLabelKey(token))
            : token
        )
        .join(', ');
    },
    [translate]
  );
};
